import { ENV } from '../../../config';

export interface WorkspaceColumn {
  columnIndex: number;
  name: string;
  isInput: boolean;
  dataType: string;
  validation: string | null;
  formula: string | null;
}

export interface WorkspaceCell {
  columnIndex: number;
  value: string;
}

export interface WorkspaceOutputRow {
  data: string | Record<string, string>;
}

export interface WorkspaceSheet {
  sheetIndex: number;
  sheetName: string;
  isInput: boolean;
  isOutput?: boolean;
  columns: WorkspaceColumn[];
  rows: WorkspaceCell[][];
  rowSources?: string[];
  outputRows?: WorkspaceOutputRow[];
}

export interface WorkspaceCalculation {
  calcProfileItemId: string;
  standardCalculationCode: string;
  standardCalculationName: string;
  category: string | null;
  templateName: string | null;
  templateVersionId: number | null;
  sortOrder: number;
  status: string;
  sheets: WorkspaceSheet[];
}

export interface ProjectWorkspace {
  projectId: number;
  entityId: number;
  projectName: string;
  partnershipName: string | null;
  taxYear: string;
  status: string;
  calculations: WorkspaceCalculation[];
}

export interface SaveRowsPayload {
  calcProfileItemId: string;
  sheetIndex: number;
  rows: WorkspaceCell[][];
}

export interface RecalculateCellChange {
  rowIndex: number;
  columnIndex: number;
  value: string;
}

export interface RecalculatePayload {
  calcProfileItemId: string;
  rows: RecalculateCellChange[];
}

export interface RecalculateCellResult {
  rowIndex: number;
  columnIndex: number;
  value: string;
  formula: string | null;
}

export interface RecalculateResponse {
  rows: RecalculateCellResult[];
}

export interface UploadResponse {
  rowsImported: number;
}

export interface TrialBalanceRow {
  id: number;
  accountId: string;
  accountName: string;
  accountType: string;
  accountClass: string;
  amount: number;
}

export interface ProjectOutputSheet {
  sheetName: string;
  calcName: string | null;
  rows: WorkspaceOutputRow[];
}

export interface ProjectOutputRows {
  projectId: number;
  sheets: ProjectOutputSheet[];
}

const BASE = `${ENV.API_URL}/api`;

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    tenant: ENV.TENANT_ID ?? '',
    'Content-Type': 'application/json',
  };
}

function authHeadersNoContentType(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    tenant: ENV.TENANT_ID ?? '',
  };
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw Object.assign(new Error('API error'), { status: r.status, data: err });
  }
  return r.json();
}

export const workspaceService = {
  getWorkspace(projectId: number | string): Promise<ProjectWorkspace> {
    return apiFetch<ProjectWorkspace>(`/project/${projectId}/workspace`);
  },

  saveRows(projectId: number | string, payload: SaveRowsPayload): Promise<void> {
    return apiFetch(`/project/${projectId}/workspace/rows`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  recalculate(projectId: number | string, payload: RecalculatePayload): Promise<RecalculateResponse> {
    return apiFetch<RecalculateResponse>(`/project/${projectId}/workspace/recalculate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  uploadData(projectId: number | string, calcProfileItemId: string, sheetIndex: number, file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('calcProfileItemId', calcProfileItemId);
    formData.append('sheetIndex', String(sheetIndex));
    return fetch(`${BASE}/project/${projectId}/workspace/upload`, {
      method: 'POST',
      headers: authHeadersNoContentType(),
      body: formData,
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw Object.assign(new Error('API error'), { status: r.status, data: err });
      }
      return r.json();
    });
  },

  downloadTemplate(templateVersionId: number): void {
    const url = `${BASE}/template/${templateVersionId}/download`;
    const a = document.createElement('a');
    a.href = url;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, tenant: ENV.TENANT_ID ?? '' } })
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.download = `template_${templateVersionId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      });
  },

  getTrialBalance(entityId: number | string): Promise<{ rows: TrialBalanceRow[] }> {
    return apiFetch<{ rows: TrialBalanceRow[] }>(`/trial-balance?entityId=${entityId}`);
  },

  getOutputRows(projectId: number | string, calcName?: string | null): Promise<ProjectOutputRows> {
    const query = calcName ? `?calcName=${encodeURIComponent(calcName)}` : '';
    return apiFetch<ProjectOutputRows>(`/project/${projectId}/workspace/output${query}`);
  },
};
